/**
 * PageUsage.jsx  —  VATICA V5
 * Client-facing page usage tracker.
 * Shows page counts only — no costs, no billing, no money.
 */

import { useState, useEffect } from "react";
import { getMyUsage } from "./api";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  .pu-wrap { padding: 28px 24px; max-width: 860px; }
  .pu-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
  .pu-title { font-size:20px; font-weight:800; color:var(--text); }
  .pu-sub   { font-size:12px; color:var(--muted); margin-top:2px; }

  .pu-month-row { display:flex; align-items:center; gap:8px; }
  .pu-month-select {
    background:var(--surface); color:var(--text); border:1px solid var(--border);
    border-radius:9px; padding:8px 12px; font-family:'Outfit',sans-serif;
    font-size:13px; font-weight:600; cursor:pointer; outline:none;
  }

  /* Hero total card */
  .pu-hero {
    background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue));
    border-radius:18px; padding:28px 32px; margin-bottom:20px;
    display:flex; align-items:center; gap:24px;
    box-shadow:0 4px 24px rgba(0,102,255,0.18);
  }
  .pu-hero-icon { font-size:44px; opacity:0.9; }
  .pu-hero-num  { font-family:'JetBrains Mono',monospace; font-size:52px; font-weight:700; color:#fff; line-height:1; }
  .pu-hero-lbl  { font-size:14px; color:rgba(255,255,255,0.75); margin-top:4px; font-weight:500; }
  .pu-hero-sub  { font-size:12px; color:rgba(255,255,255,0.55); margin-top:2px; }

  /* Breakdown cards */
  .pu-cards { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:20px; }
  @media(max-width:640px){ .pu-cards{grid-template-columns:1fr;} }
  .pu-card {
    background:var(--panel); border:1px solid var(--border);
    border-radius:14px; padding:20px 18px;
    display:flex; align-items:center; gap:14px;
  }
  .pu-card-icon {
    width:44px; height:44px; border-radius:12px; flex-shrink:0;
    display:flex; align-items:center; justify-content:center; font-size:20px;
  }
  .pu-card-num  { font-family:'JetBrains Mono',monospace; font-size:26px; font-weight:700; color:var(--text); line-height:1; }
  .pu-card-lbl  { font-size:12px; color:var(--muted); margin-top:3px; font-weight:500; }
  .pu-card-desc { font-size:11px; color:var(--muted); margin-top:2px; opacity:0.7; }

  /* Bar */
  .pu-bar-wrap { background:var(--panel); border:1px solid var(--border); border-radius:14px; padding:20px 20px; margin-bottom:20px; }
  .pu-bar-title { font-size:12px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:0.08em; margin-bottom:14px; }
  .pu-bar-track { height:18px; border-radius:9px; background:var(--surface); overflow:hidden; display:flex; margin-bottom:12px; }
  .pu-bar-seg   { height:100%; transition:width 0.6s ease; }
  .pu-bar-legend { display:flex; gap:16px; flex-wrap:wrap; }
  .pu-legend-item { display:flex; align-items:center; gap:6px; font-size:12px; color:var(--muted-light); }
  .pu-legend-dot  { width:10px; height:10px; border-radius:50%; flex-shrink:0; }

  /* History table */
  .pu-history { background:var(--panel); border:1px solid var(--border); border-radius:14px; overflow:hidden; }
  .pu-hist-header { padding:14px 18px; border-bottom:1px solid var(--border); font-size:12px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:0.08em; }
  .pu-hist-row {
    display:grid; grid-template-columns:1fr 80px 80px 80px 80px;
    padding:11px 18px; border-bottom:1px solid var(--border); align-items:center;
    font-size:13px;
  }
  .pu-hist-row:last-child { border-bottom:none; }
  .pu-hist-row.hdr { font-weight:700; color:var(--muted); font-size:11px; text-transform:uppercase; letter-spacing:0.07em; background:var(--surface); }
  .pu-hist-date { color:var(--text); font-weight:500; }
  .pu-hist-num  { font-family:'JetBrains Mono',monospace; font-size:13px; text-align:right; }

  .pu-empty { text-align:center; padding:48px 24px; color:var(--muted); }
  .pu-empty-icon { font-size:40px; opacity:0.2; margin-bottom:10px; }
  .pu-loading { text-align:center; padding:48px; color:var(--muted); font-size:13px; }
  .pu-badge { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:700; text-transform:uppercase; }
`;

const MONTHS = () => {
  const list = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const lbl = d.toLocaleDateString("en-GB", { month:"long", year:"numeric" });
    list.push({ val, lbl });
  }
  return list;
};

const fmt = n => Number(n||0).toLocaleString();

export default function PageUsage() {
  const months  = MONTHS();
  const [month, setMonth]   = useState(months[0].val);
  const [data,  setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => { load(month); }, [month]);

  async function load(m) {
    setLoading(true);
    setError("");
    try {
      const d = await getMyUsage(m);
      setData(d);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const total    = data?.total_pages    || 0;
  const purchase = data?.purchase_pages || 0;
  const sales    = data?.sales_pages    || 0;
  const bank     = data?.bank_pages     || 0;
  const invoices = data?.total_invoices || 0;

  // Bar percentages
  const pPct = total > 0 ? (purchase / total * 100).toFixed(1) : 0;
  const sPct = total > 0 ? (sales    / total * 100).toFixed(1) : 0;
  const bPct = total > 0 ? (bank     / total * 100).toFixed(1) : 0;

  const monthLabel = months.find(m2 => m2.val === month)?.lbl || month;

  return (
    <>
      <style>{CSS}</style>
      <div className="pu-wrap">

        {/* Header */}
        <div className="pu-header">
          <div>
            <div className="pu-title">📄 My Page Count</div>
            <div className="pu-sub">Every page processed by AI — invoices and bank statements</div>
          </div>
          <div className="pu-month-row">
            <select className="pu-month-select" value={month} onChange={e=>setMonth(e.target.value)}>
              {months.map(m2 => (
                <option key={m2.val} value={m2.val}>{m2.lbl}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && <div className="pu-loading">↻ Loading usage data…</div>}
        {error   && <div style={{color:"var(--error)",fontSize:13,marginBottom:16}}>⚠ {error}</div>}

        {!loading && data && (
          <>
            {/* Hero */}
            <div className="pu-hero">
              <div className="pu-hero-icon">📄</div>
              <div>
                <div className="pu-hero-num">{fmt(total)}</div>
                <div className="pu-hero-lbl">Total Pages Processed — {monthLabel}</div>
                <div className="pu-hero-sub">{fmt(invoices)} document{invoices !== 1 ? "s" : ""} across all tools</div>
              </div>
            </div>

            {/* Breakdown cards */}
            <div className="pu-cards">
              {/* Purchase */}
              <div className="pu-card">
                <div className="pu-card-icon" style={{background:"rgba(59,158,255,0.12)"}}>📋</div>
                <div>
                  <div className="pu-card-num" style={{color:"var(--ai-blue)"}}>{fmt(purchase)}</div>
                  <div className="pu-card-lbl">Purchase Invoice Pages</div>
                  <div className="pu-card-desc">VAT extraction</div>
                </div>
              </div>

              {/* Sales */}
              <div className="pu-card">
                <div className="pu-card-icon" style={{background:"rgba(34,211,168,0.12)"}}>🧾</div>
                <div>
                  <div className="pu-card-num" style={{color:"var(--success)"}}>{fmt(sales)}</div>
                  <div className="pu-card-lbl">Sales Invoice Pages</div>
                  <div className="pu-card-desc">Sales extraction</div>
                </div>
              </div>

              {/* Bank */}
              <div className="pu-card">
                <div className="pu-card-icon" style={{background:"rgba(251,191,36,0.12)"}}>🏦</div>
                <div>
                  <div className="pu-card-num" style={{color:"var(--warning)"}}>{fmt(bank)}</div>
                  <div className="pu-card-lbl">Bank Statement Pages</div>
                  <div className="pu-card-desc">Current &amp; loan accounts</div>
                </div>
              </div>
            </div>

            {/* Stacked bar */}
            {total > 0 && (
              <div className="pu-bar-wrap">
                <div className="pu-bar-title">Page breakdown</div>
                <div className="pu-bar-track">
                  <div className="pu-bar-seg" style={{width:`${pPct}%`, background:"var(--ai-blue)"}} />
                  <div className="pu-bar-seg" style={{width:`${sPct}%`, background:"var(--success)"}} />
                  <div className="pu-bar-seg" style={{width:`${bPct}%`, background:"var(--warning)"}} />
                </div>
                <div className="pu-bar-legend">
                  <div className="pu-legend-item">
                    <div className="pu-legend-dot" style={{background:"var(--ai-blue)"}}/>
                    Purchase invoices — {pPct}%
                  </div>
                  <div className="pu-legend-item">
                    <div className="pu-legend-dot" style={{background:"var(--success)"}}/>
                    Sales invoices — {sPct}%
                  </div>
                  <div className="pu-legend-item">
                    <div className="pu-legend-dot" style={{background:"var(--warning)"}}/>
                    Bank statements — {bPct}%
                  </div>
                </div>
              </div>
            )}

            {/* Zero state */}
            {total === 0 && (
              <div className="pu-empty">
                <div className="pu-empty-icon">📄</div>
                <div style={{fontWeight:600,marginBottom:4}}>No pages processed in {monthLabel}</div>
                <div style={{fontSize:12}}>Upload invoices or bank statements to see your usage here.</div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
